import { useMemo } from "react";
import { useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";
import { ClientApiError, useApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { listReportsEndpoint } from "@visioni/a3s-reports/client-messages";
import type { ListReportsResponse, ReportListItem } from "@visioni/a3s-reports/client-messages";
import { useTenant, useTenantId } from "@src/features/tenants";

/**
 * 60s feels like the right freshness window for the listing page: the
 * generator stamps a new row at most once per period (daily for the daily
 * cadence, weekly otherwise), so a fresher poll would just thrash the cache
 * for no UX gain. The query still revalidates on window focus and reconnect.
 */
const STALE_TIME_MS = 60_000;

/**
 * Page size. The endpoint caps at 100; 52 keeps a year's worth of weekly
 * rows on a single page so steady-state tenants never have to "Load more".
 * Older rows are reachable by paginating with the opaque DDB cursor that
 * each page response carries.
 */
const PAGE_LIMIT = 52;

/**
 * Lists every WasteSense report row visible to the active tenant, newest first.
 *
 * Filters to `status: "ready"` so the picker never offers a row that would
 * 409 on click (`generating` / `failed`). The handler ordering (newest-first)
 * is preserved on the wire — we don't re-sort here.
 *
 * Pagination is cursor-based: the endpoint forwards the DDB
 * `LastEvaluatedKey` as an opaque base64 `cursor`, and `useInfiniteQuery`
 * threads it through subsequent fetches. Cursor-based (vs offset) keeps the
 * list stable when the daily generator stamps a new row at the front
 * mid-scroll — the cursor still points at the same logical boundary in the
 * GSI, so pages don't desync.
 */
export interface UseWasteSenseReportListResult {
  readonly reports: readonly ReportListItem[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  readonly isTenantPending: boolean;
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  readonly fetchNextPage: () => void;
}

export function useWasteSenseReportList(): UseWasteSenseReportListResult {
  const apiConfig = useApiConfig();
  const tenantId = useTenantId();
  const { isLoading: isTenantLoading } = useTenant();

  const tenant = apiConfig.tenant;
  const enabled = !!tenant;

  const query = useInfiniteQuery<
    ListReportsResponse,
    Error,
    InfiniteData<ListReportsResponse, string | undefined>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: ["wastesense", "reports-list", tenant?.tenantId, "ready", PAGE_LIMIT] as const,
    enabled,
    staleTime: STALE_TIME_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.cursor,
    queryFn: async ({ pageParam, signal }) => {
      // `enabled` gates the call; this is a defensive narrow for the type-
      // system since `tenant` is optional on `ApiConfig`.
      if (!tenant) throw new Error("Tenant not ready");
      const path = { tenantId: tenant.tenantId, type: "wastesense" as const };
      const url =
        tenant.apiBaseUrl +
        expandEndpoint(listReportsEndpoint, {
          path,
          query: { status: "ready", limit: PAGE_LIMIT, ...(pageParam !== undefined && { cursor: pageParam }) },
        });

      const response = await apiConfig.authenticatedFetch(url, { signal });
      if (!response.ok) {
        let message = `Request failed: ${String(response.status)}`;
        try {
          const text = await response.text();
          if (text) {
            try {
              const json = JSON.parse(text) as { message?: string };
              if (json.message) message = json.message;
              else message = text;
            } catch {
              message = text;
            }
          }
        } catch {
          // fall through with default message
        }
        throw new ClientApiError(response.status, message);
      }
      const data: unknown = await response.json();
      return listReportsEndpoint.responseSchema.parse(data);
    },
  });

  const reports = useMemo<readonly ReportListItem[]>(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  return {
    reports,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error instanceof Error ? query.error : null,
    isTenantPending: isTenantLoading || !tenantId,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
  };
}
